// My context — what Marker holds for the signed-in user, and only for them (2026-09-30).
//
// Three tabs over one server table:
//   Memory      short notes the user asked Marker to remember ("I cover the Southeast").
//               Marker saves one only when asked in chat; this page is where they are
//               seen, edited and removed.
//   Workflows   named instructions the user runs by name from the chat ("run my post-demo
//               summary for Smith Dental"). Saved here or by asking Marker to save one.
//   Attachments documents uploaded to a conversation; kept 30 days, re-attachable here.
//
// Everything on this page is private to the viewer by construction: the server keys every
// row on the verified email, and there is no admin view of anyone's content.

import { useEffect, useRef, useState } from 'react'
import { deleteMyContext, fetchMyContext, upsertMyContext, uploadDocument } from '../lib/api'
import type { ChatAttachment, UserContextCaps, UserContextItem, UserContextKind } from '../lib/types'

interface MyContextProps {
  /** Drop "Run my "<title>" workflow for " into the chat composer and switch to chat. */
  onRunWorkflow?: (title: string) => void
  /** Attach an already-uploaded document to the chat and switch to it. */
  onAttachDocument?: (att: ChatAttachment) => void
}

const TABS: Array<{ key: UserContextKind; label: string }> = [
  { key: 'memory', label: 'Memory' },
  { key: 'workflow', label: 'Workflows' },
  { key: 'document', label: 'Attachments' },
]

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}

function fmtBytes(n: number | undefined): string {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function MyContext({ onRunWorkflow, onAttachDocument }: MyContextProps) {
  const [tab, setTab] = useState<UserContextKind>('memory')
  const [items, setItems] = useState<UserContextItem[]>([])
  const [caps, setCaps] = useState<UserContextCaps | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const r = await fetchMyContext()
      setItems(r.items)
      setCaps(r.caps)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void reload() }, [])

  // Every mutation goes through here so one failure message and one reload rule apply.
  async function mutate(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await reload()
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+: /, '') : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const visible = items.filter((i) => i.kind === tab)
  const counts = {
    memory: items.filter((i) => i.kind === 'memory').length,
    workflow: items.filter((i) => i.kind === 'workflow').length,
    document: items.filter((i) => i.kind === 'document').length,
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-3 border-b border-marker-border bg-marker-panel">
        <div className="text-lg font-semibold text-marker-text">My context</div>
        <div className="text-sm text-marker-muted">
          What Marker keeps for you between conversations. Only you can see any of it.
        </div>
      </div>

      <div className="px-6 pt-3 flex items-center gap-2 border-b border-marker-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              'px-3 py-1.5 text-sm rounded-t border-b-2 transition-colors ' +
              (tab === t.key
                ? 'border-marker-accent text-marker-text'
                : 'border-transparent text-marker-muted hover:text-marker-text')
            }
          >
            {t.label}
            <span className="ml-1.5 text-xs text-marker-muted">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-3xl">
          {error && (
            <div className="mb-3 bg-marker-bad/20 border border-marker-bad text-marker-bad px-3 py-2 text-sm rounded">
              {error}
            </div>
          )}
          {loading && items.length === 0 && (
            <div className="text-sm text-marker-muted">Loading…</div>
          )}

          {tab === 'memory' && (
            <MemoryTab items={visible} caps={caps} busy={busy} mutate={mutate} />
          )}
          {tab === 'workflow' && (
            <WorkflowTab items={visible} caps={caps} busy={busy} mutate={mutate} onRun={onRunWorkflow} />
          )}
          {tab === 'document' && (
            <DocumentTab items={visible} caps={caps} busy={busy} mutate={mutate} onAttach={onAttachDocument} />
          )}
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------- memory

function MemoryTab({ items, caps, busy, mutate }: {
  items: UserContextItem[]
  caps: UserContextCaps | null
  busy: boolean
  mutate: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [draft, setDraft] = useState('')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const max = caps?.memory.max_items ?? 30
  const maxChars = caps?.memory.max_chars ?? 400

  async function add() {
    const body = draft.trim()
    if (!body) return
    if (await mutate(() => upsertMyContext({ kind: 'memory', body }))) setDraft('')
  }

  // The server has no update-in-place for a memory (its key is an id, its identity is its
  // text), so an edit is save-the-new then remove-the-old.
  async function saveEdit(item: UserContextItem) {
    const body = editText.trim()
    if (!body || body === item.body) { setEditingKey(null); return }
    const ok = await mutate(async () => {
      await upsertMyContext({ kind: 'memory', body })
      await deleteMyContext('memory', item.key)
    })
    if (ok) setEditingKey(null)
  }

  return (
    <div>
      <p className="text-sm text-marker-muted mb-4">
        Short notes Marker applies to every conversation you have — your territory, how you
        like answers shaped, who your AE is. Marker saves one only when you ask it to
        (&ldquo;remember that…&rdquo;), or you can add one here.
      </p>

      {items.length === 0 && (
        <div className="text-sm text-marker-muted border border-dashed border-marker-border rounded p-4 mb-4">
          Nothing saved yet. Try telling Marker in chat: &ldquo;Remember that I cover the
          Southeast and prefer answers as five bullets.&rdquo;
        </div>
      )}

      <div className="space-y-2 mb-5">
        {items.map((item) => (
          <div key={item.key} className="border border-marker-border rounded bg-marker-panel p-3">
            {editingKey === item.key ? (
              <div>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  maxLength={maxChars}
                  rows={2}
                  className="w-full bg-marker-bg border border-marker-border rounded p-2 text-sm text-marker-text focus:outline-none focus:border-marker-accent resize-none"
                />
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <button onClick={() => void saveEdit(item)} disabled={busy} className="text-marker-accent hover:underline disabled:opacity-40">Save</button>
                  <button onClick={() => setEditingKey(null)} className="text-marker-muted hover:text-marker-text">Cancel</button>
                  <span className="ml-auto text-marker-muted">{editText.length}/{maxChars}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-marker-text whitespace-pre-wrap">{item.body}</div>
                  <div className="text-xs text-marker-muted mt-1">saved {fmtDate(item.updated_at)}</div>
                </div>
                <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                  <button
                    onClick={() => { setEditingKey(item.key); setEditText(item.body || '') }}
                    className="text-marker-muted hover:text-marker-text"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => void mutate(() => deleteMyContext('memory', item.key))}
                    disabled={busy}
                    className="text-marker-muted hover:text-marker-bad disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border border-marker-border rounded bg-marker-panel p-3">
        <div className="text-xs text-marker-muted mb-2">Add a note · {items.length} of {max}</div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={maxChars}
          rows={2}
          placeholder="e.g. I'm the AE for the Mountain West; keep my answers short."
          className="w-full bg-marker-bg border border-marker-border rounded p-2 text-sm text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent resize-none"
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            onClick={() => void add()}
            disabled={busy || !draft.trim() || items.length >= max}
            className="px-3 py-1 text-sm bg-marker-accent hover:bg-marker-accent2 disabled:bg-marker-panel2 disabled:text-marker-muted disabled:cursor-not-allowed text-white rounded"
          >
            Save
          </button>
          <span className="ml-auto text-xs text-marker-muted">{draft.length}/{maxChars}</span>
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------- workflows

function WorkflowTab({ items, caps, busy, mutate, onRun }: {
  items: UserContextItem[]
  caps: UserContextCaps | null
  busy: boolean
  mutate: (fn: () => Promise<unknown>) => Promise<boolean>
  onRun?: (title: string) => void
}) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const max = caps?.workflow.max_items ?? 10
  const maxChars = caps?.workflow.max_chars ?? 4000
  const maxTitle = caps?.workflow.max_title_chars ?? 80

  function startEdit(item: UserContextItem) {
    setEditingKey(item.key)
    setTitle(item.title || '')
    setBody(item.body || '')
  }

  function cancelEdit() {
    setEditingKey(null)
    setTitle('')
    setBody('')
  }

  // Saving under the same name overwrites (the key is the slug of the title). A renamed
  // workflow is a new key, so the old one is removed once the new one has saved.
  async function save() {
    const t = title.trim()
    const b = body.trim()
    if (!t || !b) return
    const old = editingKey ? items.find((i) => i.key === editingKey) : undefined
    const ok = await mutate(async () => {
      const r = await upsertMyContext({ kind: 'workflow', body: b, title: t })
      if (old && r.item.key !== old.key) await deleteMyContext('workflow', old.key)
    })
    if (ok) cancelEdit()
  }

  const form = (
    <div className="border border-marker-border rounded bg-marker-panel p-3">
      <div className="text-xs text-marker-muted mb-2">
        {editingKey ? 'Edit workflow' : `New workflow · ${items.length} of ${max}`}
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={maxTitle}
        placeholder="Name — what you'll say to run it, e.g. post-demo summary"
        className="w-full mb-2 bg-marker-bg border border-marker-border rounded p-2 text-sm text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={maxChars}
        rows={6}
        placeholder={'What Marker should do, in your words. e.g.\nFor the demo I name: 1) three-sentence summary, 2) every objection with how it was handled, 3) open questions, 4) next steps with owners. Plain language, no headers, under 250 words.'}
        className="w-full bg-marker-bg border border-marker-border rounded p-2 text-sm text-marker-text placeholder:text-marker-muted focus:outline-none focus:border-marker-accent resize-y"
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={() => void save()}
          disabled={busy || !title.trim() || !body.trim() || (!editingKey && items.length >= max)}
          className="px-3 py-1 text-sm bg-marker-accent hover:bg-marker-accent2 disabled:bg-marker-panel2 disabled:text-marker-muted disabled:cursor-not-allowed text-white rounded"
        >
          {editingKey ? 'Save changes' : 'Save workflow'}
        </button>
        {editingKey && (
          <button onClick={cancelEdit} className="text-xs text-marker-muted hover:text-marker-text">Cancel</button>
        )}
        <span className="ml-auto text-xs text-marker-muted">{body.length}/{maxChars}</span>
      </div>
    </div>
  )

  return (
    <div>
      <p className="text-sm text-marker-muted mb-4">
        A workflow is a set of instructions you run by name from the chat — &ldquo;run my
        post-demo summary for Smith Dental&rdquo; — without describing the format again.
        Marker follows it with its ordinary tools, so each run is fresh, not a fill-in form.
        Save one here, or tell Marker in chat: &ldquo;save this as my post-demo summary.&rdquo;
      </p>

      {items.length === 0 && (
        <div className="text-sm text-marker-muted border border-dashed border-marker-border rounded p-4 mb-4">
          No workflows yet.
        </div>
      )}

      <div className="space-y-2 mb-5">
        {items.map((item) => (
          <div key={item.key} className="border border-marker-border rounded bg-marker-panel p-3">
            {editingKey === item.key ? form : (
              <div>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => setOpenKey(openKey === item.key ? null : item.key)}
                    className="flex-1 min-w-0 text-left"
                    title="Show the instructions"
                  >
                    <div className="text-sm font-medium text-marker-text truncate">
                      <span className="text-marker-muted mr-1">{openKey === item.key ? '▾' : '▸'}</span>
                      {item.title}
                    </div>
                    <div className="text-xs text-marker-muted mt-0.5">
                      saved {fmtDate(item.updated_at)} · run it: &ldquo;run my {item.title} for …&rdquo;
                    </div>
                  </button>
                  <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                    {onRun && (
                      <button
                        onClick={() => onRun(item.title || '')}
                        className="px-2 py-0.5 rounded border border-marker-accent text-marker-accent hover:bg-marker-accent/10"
                        title="Open the chat with this workflow ready to run"
                      >
                        Run in chat
                      </button>
                    )}
                    <button onClick={() => startEdit(item)} className="text-marker-muted hover:text-marker-text">Edit</button>
                    <button
                      onClick={() => void mutate(() => deleteMyContext('workflow', item.key))}
                      disabled={busy}
                      className="text-marker-muted hover:text-marker-bad disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {openKey === item.key && (
                  <pre className="mt-2 text-xs text-marker-text whitespace-pre-wrap font-sans bg-marker-bg border border-marker-border rounded p-2">
                    {item.body}
                  </pre>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!editingKey && form}
    </div>
  )
}

// ----------------------------------------------------------------------------- documents

function DocumentTab({ items, caps, busy, mutate, onAttach }: {
  items: UserContextItem[]
  caps: UserContextCaps | null
  busy: boolean
  mutate: (fn: () => Promise<unknown>) => Promise<boolean>
  onAttach?: (att: ChatAttachment) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const accept = (caps?.document.extensions ?? ['pdf', 'docx', 'txt', 'md']).map((e) => `.${e}`).join(',')
  const retention = caps?.document.retention_days ?? 30
  const maxMb = Math.round((caps?.document.max_bytes ?? 15 * 1024 * 1024) / 1024 / 1024)

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    await mutate(() => uploadDocument(f))
  }

  return (
    <div>
      <p className="text-sm text-marker-muted mb-4">
        Documents you have attached to a conversation — a new-feature one-pager, a pricing
        sheet. Marker reads the text and puts it in front of the model for that chat. Kept
        for {retention} days; PDF, Word, text or markdown up to {maxMb} MB. Scanned PDFs
        (images only) cannot be read.
      </p>

      <div className="mb-4">
        <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={(e) => void onPick(e)} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="px-3 py-1 text-sm bg-marker-accent hover:bg-marker-accent2 disabled:bg-marker-panel2 disabled:text-marker-muted text-white rounded"
        >
          {busy ? 'Working…' : 'Upload a document'}
        </button>
        <span className="ml-3 text-xs text-marker-muted">or use the 📎 in the chat composer</span>
      </div>

      {items.length === 0 && (
        <div className="text-sm text-marker-muted border border-dashed border-marker-border rounded p-4">
          No attachments yet.
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => {
          const m = item.meta || {}
          return (
            <div key={item.key} className="border border-marker-border rounded bg-marker-panel p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-marker-text truncate">📎 {item.title}</div>
                <div className="text-xs text-marker-muted mt-0.5">
                  {[
                    m.ext?.toUpperCase(),
                    fmtBytes(m.bytes),
                    m.pages ? `${m.pages} page${m.pages === 1 ? '' : 's'}` : null,
                    item.chars ? `~${Math.round(item.chars / 4).toLocaleString()} tokens` : null,
                    m.truncated ? 'cut off at the size limit' : null,
                    item.expires_at ? `expires ${fmtDate(item.expires_at)}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                {onAttach && (
                  <button
                    onClick={() => onAttach({
                      documentId: item.key,
                      title: item.title || 'document',
                      approxTokens: item.chars ? Math.round(item.chars / 4) : undefined,
                      truncated: !!m.truncated,
                    })}
                    className="px-2 py-0.5 rounded border border-marker-accent text-marker-accent hover:bg-marker-accent/10"
                  >
                    Attach to chat
                  </button>
                )}
                <button
                  onClick={() => void mutate(() => deleteMyContext('document', item.key))}
                  disabled={busy}
                  className="text-marker-muted hover:text-marker-bad disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
